// Site-wide SEO primitives. `utils/guideSeo.ts` builds the game-guide tree on
// top of these; every other page consumes them through <PageSeo>.
//
// Thai-first by design: the audience searches in Thai but types entity names
// (game titles, character names, platform names) in Latin script, so metadata
// pairs Thai intent words with untranslated English entities rather than
// translating everything.

// ─── Site constants ───────────────────────────────────────────────────────────
// The production origin. Every canonical/OG/sitemap URL must derive from this —
// never from window.location (leaks query strings) or hardcoded literals.
export const SITE_URL = (process.env.NEXT_PUBLIC_SITE_URL || 'https://games-on.net').replace(/\/+$/, '')
export const SITE_NAME = "Game's On"
export const SITE_SHARE_IMAGE = `${SITE_URL}/gameson-og-image.webp`
export const SITE_LOGO = `${SITE_URL}/icons/icon-512x512.png`
/** X/Twitter handle for twitter:site — the spec wants an @handle, not a URL. */
export const TWITTER_HANDLE = '@GamesOnOfficial'

export const SITE_SOCIALS = [
  'https://www.facebook.com/GamesOnOfficial',
  'https://www.instagram.com/g.games.on/',
  'https://twitter.com/GamesOnOfficial',
  'https://www.youtube.com/@Games-On-TH',
  'https://www.tiktok.com/@gamesonofficial',
]

export const absoluteUrl = (path: string) =>
  path.startsWith('http') ? path : `${SITE_URL}${path.startsWith('/') ? '' : '/'}${path}`

// ─── Text helpers ─────────────────────────────────────────────────────────────
export const stripHtmlToText = (html?: string) =>
  (html || '')
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<\/(p|div|li|h[1-6])>/gi, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim()

export const truncate = (text: string, max: number) =>
  text.length > max ? `${text.slice(0, Math.max(0, max - 1)).trimEnd()}…` : text

export const latestDate = (...dates: (string | undefined)[]) => {
  const times = dates
    .filter(Boolean)
    .map((d) => new Date(d as string).getTime())
    .filter((t) => !Number.isNaN(t))
  return times.length ? new Date(Math.max(...times)).toISOString() : undefined
}

// ─── SEO data shape consumed by <PageSeo> / <GuideSeo> ────────────────────────
export type SeoData = {
  title: string
  description: string
  keywords?: string[]
  /** Clean canonical path ("/game/zzz"), no query strings. */
  canonicalPath: string
  ogType: 'website' | 'article'
  ogImage: string
  ogImageAlt: string
  /** True when entity data is missing or the page has no search value. */
  noindex?: boolean
  jsonLd?: Record<string, any>[]
}

// ─── Shared JSON-LD fragments ─────────────────────────────────────────────────
export const publisherLd = {
  '@type': 'Organization',
  name: SITE_NAME,
  url: SITE_URL,
  logo: { '@type': 'ImageObject', url: SITE_LOGO },
}

export const breadcrumbLd = (items: { name: string; path?: string }[]) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((item, i) => ({
    '@type': 'ListItem',
    position: i + 1,
    name: item.name,
    ...(item.path ? { item: absoluteUrl(item.path) } : {}),
  })),
})

export const articleLd = (opts: {
  headline: string
  description: string
  canonicalPath: string
  image?: string
  datePublished?: string
  dateModified?: string
  about?: Record<string, any>
}) => ({
  '@context': 'https://schema.org',
  '@type': 'Article',
  mainEntityOfPage: { '@type': 'WebPage', '@id': absoluteUrl(opts.canonicalPath) },
  headline: truncate(opts.headline, 110),
  description: opts.description,
  ...(opts.image ? { image: opts.image } : {}),
  inLanguage: 'th',
  ...(opts.datePublished ? { datePublished: opts.datePublished } : {}),
  ...(opts.dateModified ? { dateModified: opts.dateModified } : {}),
  ...(opts.about ? { about: opts.about } : {}),
  author: { '@type': 'Organization', name: SITE_NAME, url: SITE_URL },
  publisher: publisherLd,
})

/**
 * CollectionPage for a listing/hub route. `itemUrls` (optional) exposes the
 * listed items as an ItemList so Google can see the page has real inventory
 * instead of guessing from client-rendered cards.
 */
export const collectionPageLd = (opts: {
  name: string
  description: string
  canonicalPath: string
  itemUrls?: string[]
}) => ({
  '@context': 'https://schema.org',
  '@type': 'CollectionPage',
  name: opts.name,
  description: opts.description,
  url: absoluteUrl(opts.canonicalPath),
  inLanguage: 'th',
  isPartOf: { '@type': 'WebSite', '@id': `${SITE_URL}/#website` },
  publisher: publisherLd,
  ...(opts.itemUrls?.length
    ? {
        mainEntity: {
          '@type': 'ItemList',
          numberOfItems: opts.itemUrls.length,
          itemListElement: opts.itemUrls.map((url, i) => ({
            '@type': 'ListItem',
            position: i + 1,
            url: absoluteUrl(url),
          })),
        },
      }
    : {}),
})

// ─── Listing-page metadata ────────────────────────────────────────────────────
/**
 * Builds SEO for a site-section listing page. Titles stay under ~60 characters
 * so Google renders them in full; the old 200–280 character keyword dumps were
 * truncated to nothing useful and duplicated verbatim into the description.
 */
export const buildListingSeo = (opts: {
  /** Thai-primary headline, e.g. "ข่าวเกม" */
  title: string
  description: string
  canonicalPath: string
  keywords?: string[]
  breadcrumb?: { name: string; path?: string }[]
  itemUrls?: string[]
  noindex?: boolean
}): SeoData => {
  const title = `${opts.title} | ${SITE_NAME}`
  return {
    title,
    description: truncate(opts.description, 300),
    keywords: opts.keywords,
    canonicalPath: opts.canonicalPath,
    ogType: 'website',
    ogImage: SITE_SHARE_IMAGE,
    ogImageAlt: `${opts.title} — ${SITE_NAME}`,
    noindex: opts.noindex,
    jsonLd: opts.noindex
      ? []
      : [
          collectionPageLd({
            name: title,
            description: truncate(opts.description, 300),
            canonicalPath: opts.canonicalPath,
            itemUrls: opts.itemUrls,
          }),
          breadcrumbLd([
            { name: 'หน้าแรก', path: '/' },
            ...(opts.breadcrumb ?? [{ name: opts.title }]),
          ]),
        ],
  }
}
